// app/features/industry-opportunities/industry-opportunity.utils.ts
// Shared utilities for industry opportunities - can be used in both server and client components


import type { IndustryOpportunity } from './industry-opportunity.interface';

// Category configuration with colors and icons
// Color order: blue → terracotta → green → purple → teal → indigo → gold
export const CATEGORY_CONFIG: Record<string, { 
  label: string; 
  accentColor: string; 
  bgColor: string;
  textColor: string;
  hoverTextColor: string;
  iconName: string;
}> = {
  'advisor-lifecycle': {
    label: 'Advisor Lifecycle',
    accentColor: 'bg-purple-dark',
    bgColor: 'bg-purple-dark/10',
    textColor: 'text-purple-dark',
    hoverTextColor: 'group-hover:text-purple-dark',
    iconName: 'Users',
  },
  'compliance': {
    label: 'Compliance',
    accentColor: 'bg-orange',
    bgColor: 'bg-orange/10',
    textColor: 'text-orange',
    hoverTextColor: 'group-hover:text-orange',
    iconName: 'Shield',
  },
  'client-communication': {
    label: 'Client Communication',
    accentColor: 'bg-blue',
    bgColor: 'bg-blue/10',
    textColor: 'text-blue',
    hoverTextColor: 'group-hover:text-blue-dark',
    iconName: 'Users',
  },
  'client-updates': {
    label: 'Client Updates',
    accentColor: 'bg-terracotta',
    bgColor: 'bg-terracotta/10',
    textColor: 'text-terracotta',
    hoverTextColor: 'group-hover:text-terracotta-dark',
    iconName: 'Zap',
  },
  'reporting': {
    label: 'Reporting',
    accentColor: 'bg-green',
    bgColor: 'bg-green/10',
    textColor: 'text-green',
    hoverTextColor: 'group-hover:text-green-dark',
    iconName: 'BarChart3',
  },
  'operations': {
    label: 'Operations',
    accentColor: 'bg-teal',
    bgColor: 'bg-teal/10',
    textColor: 'text-teal',
    hoverTextColor: 'group-hover:text-teal',
    iconName: 'Settings',
  },
  'data-search': {
    label: 'Data & Search',
    accentColor: 'bg-indigo',
    bgColor: 'bg-indigo/10',
    textColor: 'text-indigo',
    hoverTextColor: 'group-hover:text-indigo',
    iconName: 'Database',
  },
  'onboarding': {
    label: 'Onboarding',
    accentColor: 'bg-brown',
    bgColor: 'bg-brown/10',
    textColor: 'text-brown',
    hoverTextColor: 'group-hover:text-brown',
    iconName: 'FileText',
  },
  'planning': {
    label: 'Planning & Intelligence',
    accentColor: 'bg-purple-dark',
    bgColor: 'bg-purple-dark/10',
    textColor: 'text-purple-dark',
    hoverTextColor: 'group-hover:text-purple-dark',
    iconName: 'FileText',
  },
  'growth': {
    label: 'Go to Market & Growth',
    accentColor: 'bg-yellow-dark',
    bgColor: 'bg-yellow-dark/10',
    textColor: 'text-yellow-dark',
    hoverTextColor: 'group-hover:text-yellow-dark',
    iconName: 'Zap',
  },
  'custom': {
    label: 'Specialized',
    accentColor: 'bg-gold',
    bgColor: 'bg-gold/10',
    textColor: 'text-gold',
    hoverTextColor: 'group-hover:text-gold',
    iconName: 'Sparkles',
  },
};

// Get category key from opportunity - can be called from server or client
export function getCategoryKey(opportunity: IndustryOpportunity): string {
  // Check metadata_json for explicit sub_vertical first
  if (opportunity.metadata_json && typeof opportunity.metadata_json === 'object' && 'sub_vertical' in opportunity.metadata_json) {
    const subVertical = opportunity.metadata_json.sub_vertical;
    if (typeof subVertical === 'string' && subVertical.length > 0) {
      return subVertical;
    }
  }
  
  // Try linked product slug
  if (opportunity.linked_product_slug) {
    const slug = opportunity.linked_product_slug;
    if (slug.includes('account-manager') || slug.includes('newsletter')) return 'client-communication';
    if (slug.includes('report')) return 'reporting';
    if (slug.includes('onboard')) return 'onboarding';
    if (slug.includes('data') || slug.includes('intelligence')) return 'data-search';
    if (slug.includes('operations')) return 'operations';
  }
  
  // Try to infer from problem title
  const title = opportunity.problem_title.toLowerCase();
  if (title.includes('advisor') && (title.includes('onboard') || title.includes('offboard') || title.includes('recruit') || title.includes('acqui') || title.includes('roster') || title.includes('dashboard'))) return 'advisor-lifecycle';
  if (title.includes('compliance') || title.includes('marketing review') || title.includes('form adv') || title.includes('sec exam') || title.includes('reg s-p') || title.includes('regulatory') || title.includes('complaint') || title.includes('books and records') || title.includes('surveillance') || title.includes('attestation')) return 'compliance';
  if (title.includes('client') && (title.includes('communicat') || title.includes('neglect') || title.includes('portal'))) return 'client-communication';
  if (title.includes('report') || title.includes('quarterly')) return 'reporting';
  if (title.includes('onboard')) return 'onboarding';
  if (title.includes('data') || title.includes('scatter') || title.includes('disconnect')) return 'data-search';
  if (title.includes('fee') || title.includes('rebalanc') || title.includes('401')) return 'operations';
  if (title.includes('meeting') || title.includes('intelligence')) return 'client-updates';
  if (title.includes('tax') || title.includes('estate') || title.includes('plan') || title.includes('scenario')) return 'operations';
  if (title.includes('billing') || title.includes('rebalanc') || title.includes('payout')) return 'operations';
  if (title.includes('growth') || title.includes('prospect') || title.includes('referral') || title.includes('acqui')) return 'growth';
  
  return 'custom';
}

// Category display order
export const CATEGORY_ORDER = [
  'advisor-lifecycle',
  'compliance',
  'operations',
  'client-communication',
  'data-search',
  'reporting',
  'onboarding',
  'client-updates',
  'growth',
  'custom',
];

// Group opportunities by category
export function groupOpportunitiesByCategory(opportunities: IndustryOpportunity[]): Record<string, IndustryOpportunity[]> {
  return opportunities.reduce((acc, opp) => {
    const key = getCategoryKey(opp);
    if (!acc[key]) acc[key] = [];
    acc[key].push(opp);
    return acc;
  }, {} as Record<string, IndustryOpportunity[]>);
}

